import { useState } from "react";
import { Link, Outlet, createFileRoute, useMatches } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApps, initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, signInWithPopup } from "firebase/auth";
import { devLogin, firebaseLogin, getFirebaseConfig, getMe, isLocalDev, logout } from "../lib/hostApi";
import { LangToggle } from "../components/LangToggle";
import { Logo } from "../components/Logo";
import { useT } from "../lib/i18n";

export const Route = createFileRoute("/host")({ component: HostLayout });

function HostLayout() {
  const { t } = useT();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: getMe, retry: false });
  // Halaman acara & pengaturan punya layar gelap sendiri, tanpa header host.
  const bare = useMatches({
    select: (ms) => ms.some((m) => m.routeId === "/host/albums/$id/" || m.routeId === "/host/albums/$id/settings"),
  });

  if (me.isPending) return <Shell>{t("common.loading")}</Shell>;
  if (me.error || !me.data) return <Shell>{<LoginCard onDone={() => qc.invalidateQueries()} />}</Shell>;

  if (bare) return <Outlet />;
  return (
    <Shell>
      <header className="flex items-center justify-between gap-2 border-b border-sand/60 pb-3">
        <Link to="/" className="text-cocoa">
          <Logo className="h-8" />
        </Link>
        <div className="flex items-center gap-3">
          <LangToggle />
          <span className="truncate text-sm text-neutral-600">{me.data.name ?? me.data.email}</span>
          <button
            type="button"
            className="text-sm text-neutral-500 underline"
            onClick={async () => {
              await logout();
              qc.clear();
              location.href = "/host";
            }}
          >
            {t("host.logout")}
          </button>
        </div>
      </header>
      <Outlet />
    </Shell>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto min-h-dvh w-full max-w-2xl space-y-4 px-4 py-6">{children}</main>;
}

function LoginCard({ onDone }: { onDone: () => void }) {
  const { t } = useT();
  const [email, setEmail] = useState("host@contoh.com");
  const dev = useMutation({ mutationFn: () => devLogin(email, "Host Dev"), onSuccess: onDone });
  const google = useMutation({
    mutationFn: async () => {
      const auth = getAuth(getApps()[0] ?? initializeApp(await getFirebaseConfig()));
      const { user } = await signInWithPopup(auth, new GoogleAuthProvider());
      return firebaseLogin(await user.getIdToken());
    },
    onSuccess: onDone,
  });

  return (
    <div className="space-y-6 pt-16 text-center">
      <LangToggle className="justify-end" />
      <h1 className="text-cocoa">
        <Link to="/">
          <Logo className="h-14" />
        </Link>
      </h1>
      <p className="text-neutral-600">{t("host.login.subtitle")}</p>
      <button type="button" className="btn-primary w-full" disabled={google.isPending} onClick={() => google.mutate()}>
        {google.isPending ? t("host.login.opening_google") : t("host.login.google")}
      </button>
      {google.error && <p className="text-sm text-red-600">{t("host.login.google_failed")}</p>}
      {isLocalDev() && (
        <form
          className="space-y-2 rounded-2xl border border-dashed border-sand p-3 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            dev.mutate();
          }}
        >
          <p className="text-xs text-neutral-500">{t("host.login.dev_mode")}</p>
          <input className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" className="btn-secondary w-full px-4 py-2 text-sm" disabled={dev.isPending}>
            {t("host.login.dev_button")}
          </button>
        </form>
      )}
      <Link to="/" className="block text-sm text-neutral-500 underline">
        {t("host.login.back_home")}
      </Link>
    </div>
  );
}
